import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Copy, Trash2, Upload, Check, ImageIcon } from "lucide-react";

const BUCKET = "blog-covers";
const FOLDERS = ["", "inline", "library"];
const TEN_YEARS = 60 * 60 * 24 * 365 * 10;

export type MediaItem = { path: string; name: string; url: string; created_at: string; size: number };

const loadMedia = async (): Promise<MediaItem[]> => {
  const lists = await Promise.all(
    FOLDERS.map(async (f) => {
      const { data } = await supabase.storage.from(BUCKET).list(f, { limit: 1000, sortBy: { column: "created_at", order: "desc" } });
      return (data ?? [])
        .filter((o) => o.id && /\.(png|jpe?g|webp|gif|svg|avif)$/i.test(o.name))
        .map((o) => ({ path: f ? `${f}/${o.name}` : o.name, name: o.name, created_at: o.created_at ?? "", size: (o.metadata as { size?: number } | null)?.size ?? 0 }));
    })
  );
  const files = lists.flat();
  if (!files.length) return [];
  const { data } = await supabase.storage.from(BUCKET).createSignedUrls(files.map((f) => f.path), TEN_YEARS);
  const urls = new Map((data ?? []).map((d) => [d.path, d.signedUrl]));
  return files
    .map((f) => ({ ...f, url: urls.get(f.path) ?? "" }))
    .filter((f) => f.url)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
};

export const uploadMedia = async (file: File): Promise<MediaItem | null> => {
  const safe = file.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9.]+/g, "-");
  const path = `library/${Date.now()}-${safe}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file);
  if (error) throw error;
  const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, TEN_YEARS);
  return data?.signedUrl ? { path, name: safe, url: data.signedUrl, created_at: new Date().toISOString(), size: file.size } : null;
};

const useMedia = () => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => { setLoading(true); setItems(await loadMedia()); setLoading(false); }, []);
  useEffect(() => { reload(); }, [reload]);
  return { items, loading, reload };
};

const MediaGrid = ({ onPick, manage }: { onPick?: (m: MediaItem) => void; manage?: boolean }) => {
  const { toast } = useToast();
  const { items, loading, reload } = useMedia();
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [zoom, setZoom] = useState<MediaItem | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (files: FileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) await uploadMedia(f);
      toast({ title: files.length > 1 ? `${files.length} imagens enviadas` : "Imagem enviada" });
      await reload();
    } catch (e) {
      toast({ title: "Erro no upload", description: (e as Error).message, variant: "destructive" });
    }
    setUploading(false);
  };

  const remove = async (m: MediaItem) => {
    if (!window.confirm(`Excluir "${m.name}"? Artigos que usam esta imagem deixarão de exibi-la.`)) return;
    const { error } = await supabase.storage.from(BUCKET).remove([m.path]);
    if (error) toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    else reload();
  };

  const copy = async (m: MediaItem) => {
    await navigator.clipboard.writeText(m.url);
    setCopied(m.path);
    setTimeout(() => setCopied(null), 1500);
  };

  const shown = items.filter((m) => m.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="Buscar por nome..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) upload(e.target.files); e.target.value = ""; }} />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 ml-auto">
          <Upload size={16} /> {uploading ? "Enviando..." : "Enviar imagens"}
        </Button>
      </div>
      {loading ? (
        <p className="text-muted-foreground text-sm">Carregando mídia...</p>
      ) : shown.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border-2 border-dashed border-border rounded-xl">
          <ImageIcon className="mx-auto mb-2" /> Nenhuma imagem ainda.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {shown.map((m) => (
            <div key={m.path} className="group relative rounded-xl overflow-hidden border border-border bg-muted aspect-square">
              <button type="button" onClick={() => (onPick ? onPick(m) : setZoom(m))} className="block w-full h-full" title={onPick ? "Usar esta imagem" : "Ver"}>
                <img src={m.url} alt={m.name} loading="lazy" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
              </button>
              <div className="absolute inset-x-0 bottom-0 p-1.5 flex items-center gap-1 bg-gradient-to-t from-foreground/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="flex-1 truncate text-[10px] text-background">{m.name}</span>
                <button type="button" onClick={() => copy(m)} title="Copiar link" className="p-1 rounded bg-background/90 hover:text-primary">
                  {copied === m.path ? <Check size={12} /> : <Copy size={12} />}
                </button>
                {manage && (
                  <button type="button" onClick={() => remove(m)} title="Excluir" className="p-1 rounded bg-background/90 hover:text-destructive"><Trash2 size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="truncate">{zoom?.name}</DialogTitle></DialogHeader>
          {zoom && <img src={zoom.url} alt={zoom.name} className="w-full rounded-lg max-h-[70vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MediaManager = () => <MediaGrid manage />;

export const MediaPicker = ({ open, onOpenChange, onPick }: { open: boolean; onOpenChange: (o: boolean) => void; onPick: (m: MediaItem) => void }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Biblioteca de mídia</DialogTitle></DialogHeader>
      {open && <MediaGrid onPick={(m) => { onPick(m); onOpenChange(false); }} />}
    </DialogContent>
  </Dialog>
);
